import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from 'reactstrap';

import { modelOf } from '../../../prop-types';
import globalTranslations from '../../../i18n/globalTranslations';
import PageTitle from '../../common/PageTitle';
import Icon from '../../common/Icon';
import WysiwygContent from '../../common/WysiwygContent';
import OrderTotalClass from '../../../types/OrderTotalClass';
import ProductImageModel from '../../../models/ProductImage';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import safeCloneDeep from '../../../util/safeCloneDeep';
import ProposalStore from '../../../store/ProposalStore';
import Price from '../../product/Price';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import UIStore from '../../../store/UIStore';
import ProductImage from '../../product/ProductImage';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import CustomForm from '../../form/CustomForm';
import CustomFormField from '../../../models/CustomFormField';
import FormFieldId from '../../../types/FormFieldId';
import RequestState from '../../../types/RequestState';
import ViewBreakpoint from '../../../types/ViewBreakpoint';

const ProductHeaders = {
  name: <FormattedMessage {...globalTranslations.productNameTitle} />,
  RRP: <FormattedMessage {...globalTranslations.RRP} />,
  quantity: <FormattedMessage {...globalTranslations.AmountAndInTotal} />,
};

@observer
export class ProposalCheckoutPageContent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      confirmState: RequestState.NONE,
      currentOrder: safeCloneDeep(this.props.proposalStore.proposal),
      unsentChanges: false,
      contactValid: false,
    };

    this.form = null;
    this.updateTimer = null;
    this.initializeForm();
  }

  componentWillUnmount() {
    clearTimeout(this.updateTimer);
  }

  initializeForm = () => {
    const { proposal } = this.props.proposalStore;
    let formFields = {};

    if (proposal.products.length > 0) {
      proposal.products.forEach((product) => {
        const quantityFieldName = `quantity:${product.id}`;
        const step = String(product.getQuantityStep(product));
        const max = 99999;

        formFields[quantityFieldName] = createFormField(
          {
            value: step,
          },
          [quantityValid(max, step), productCountValid(step)]
        );
      });
    }

    this.form = createForm(formFields);
    this.form.validate();
  };

  disableTimer = () => {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
  };

  enableTimer = () => {
    const { onOrderUpdate } = this.props;
    const { currentOrder } = this.state;

    this.disableTimer();

    this.updateTimer = window.setTimeout(() => {
      onOrderUpdate(currentOrder);
      this.disableTimer();
      this.setState({
        unsentChanges: false,
      });
    }, 2000);
  };

  confirmProposal = () => {
    const { onOrderConfirm } = this.props;
    const { currentOrder } = this.state;

    onOrderConfirm(currentOrder);
  };

  updateProductQuantity = (id, value) => {
    const { currentOrder } = this.state;

    currentOrder.products.forEach((product) => {
      if (product.id === id) {
        const packageSize = product.package_size ? product.package_size : 1;
        product.quantity = value / packageSize;
      }
    });

    this.enableTimer();
    this.setState({
      currentOrder,
      unsentChanges: true,
    });
  };

  updateQuantity = (fieldName, value) => {
    this.updateProductQuantity(this.parseProductId(fieldName), value);
  };

  updateContactInformation = (field, formValid) => {
    const { currentOrder } = this.state;

    currentOrder.customer_fields[field.externalIndex].value = field.value;
    this.setState({
      currentOrder,
      unsentChanges: true,
    });

    if (formValid) {
      this.enableTimer();
    }
  };

  updateExtraField = (field) => {
    const { currentOrder } = this.state;

    currentOrder.extra_fields[field.externalIndex].value = field.value;
    this.setState({
      currentOrder,
      unsentChanges: true,
    });

    this.enableTimer();
  };

  removeProduct = (id) => {
    const { currentOrder } = this.state;

    const deleteIndex = currentOrder.products.findIndex(
      (product) => product.id === id
    );
    if (deleteIndex !== -1) {
      const product = currentOrder.products.slice(deleteIndex, deleteIndex + 1);
      const value = product[0].total_price;

      currentOrder.products.splice(deleteIndex, 1);

      this.setState({
        currentOrder,
      });

      this.props.onProductRemove({ id }, { value, productList: product });
    }
  };

  parseProductId = (string) => {
    return string.split('-')[1];
  };

  renderTopWysiwyg = () => {
    const { proposalStore } = this.props;
    const content = proposalStore.proposal.header_content;

    if (!content) {
      return null;
    }

    return <WysiwygContent html={content} />;
  };

  renderEmptyProposal = () => (
    <FormattedMessage
      id="checkout.emptyProposalSentence"
      defaultMessage="Proposal is empty! Products can be added via product page and listings."
    />
  );

  renderProductList = () => {
    return (
      <Card
        id="printable"
        className="ProposalCheckoutPageContent__products-card"
      >
        <CardHeader className="ProposalCheckoutPageContent__products-header">
          {'1. '}
          <FormattedMessage
            id="checkout.proposalProductsHeader"
            defaultMessage="The chosen products"
          />
        </CardHeader>
        <CardBody className="ProposalCheckoutPageContent__products-body">
          {this.renderProducts()}
        </CardBody>
      </Card>
    );
  };

  renderProposalForm = () => {
    return (
      <Card className="ProposalCheckoutPageContent__form-card">
        <CardHeader>
          {'2. '}
          <FormattedMessage
            id="checkout.proposalFormHeader"
            defaultMessage="Your contact information"
          />
        </CardHeader>
        <CardBody className="ProposalCheckoutPageContent__form-body">
          {this.getForm()}
        </CardBody>
      </Card>
    );
  };

  printElement = () => window.print();

  renderProducts = () => {
    const { uiStore, proposalStore } = this.props;
    const { proposal } = proposalStore;
    const { products } = proposal;
    const showPrices = proposal.show_prices;

    return (
      <>
        <div className="ProposalCheckoutPageContent__products">
          {uiStore.isTablet && (
            <Row className="ProposalCheckoutPageContent__product-headers">
              {this.renderProductHeaderCol(ProductHeaders.name, {
                md: 7,
                lg: 8,
                xl: 8,
              })}
              {this.renderProductHeaderCol(ProductHeaders.quantity, {
                md: 5,
                lg: 4,
                xl: 4,
              })}
            </Row>
          )}
          {uiStore.isDesktop && (
            <Row className="ProposalCheckoutPageContent__product-headers">
              {this.renderProductHeaderCol(ProductHeaders.name, {
                lg: showPrices ? 7 : 8,
              })}
              {showPrices &&
                this.renderProductHeaderCol(ProductHeaders.RRP, {
                  lg: 1,
                })}
              {this.renderProductHeaderCol(ProductHeaders.quantity, {
                lg: 4,
              })}
            </Row>
          )}
          {products.map((product, index) => {
            return this.renderProductRow(product, index);
          })}
        </div>
        <Row className="ProposalCheckoutPageContent__totals">
          <Col
            sm={12}
            md={6}
            className="ProposalCheckoutPageContent__print-button"
          >
            <Button
              className="ProposalCheckoutPageContent__print"
              color="secondary"
              tabIndex="0"
              onClick={() => this.printElement()}
            >
              <Icon name="print" />
              <FormattedMessage {...globalTranslations.productPdfTitle} />
            </Button>
          </Col>
          <Col
            sm={12}
            md={6}
            className="ProposalCheckoutPageContent__total-sum"
          >
            {proposal.totals
              .filter((total) => total.class === OrderTotalClass.TOTAL)
              .map((total) => {
                return (
                  <div key={total.class}>
                    {total.name + ' '}
                    {total.text}
                  </div>
                );
              })}
          </Col>
        </Row>
      </>
    );
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 3,
      },
      sizes
    );
    return (
      <Col
        className="ProposalCheckoutPageContent__product-header"
        {...actualSizes}
      >
        {label}
      </Col>
    );
  };

  createProductImage = (url, id) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: id,
          sizes: {
            small: url,
          },
        })
      : null;
  };

  renderProductLink = (product) => {
    return (
      product.url && (
        <MaybeExternalLink to={product.url}>
          {product.actual_code} - <span>{product.name}</span>
        </MaybeExternalLink>
      )
    );
  };

  renderProductRow = (product, index) => {
    const { proposalStore, uiStore } = this.props;
    const { activeBreakpoint, isMobile, isDesktop } = uiStore;
    const { proposal } = proposalStore;
    const showPrices = proposal.show_prices;
    const quantityField = this.form.fields.get(`quantity:${product.id}`);
    const smallScreen = activeBreakpoint === ViewBreakpoint.SM;
    const defaultValue = product.quantity * product.getQuantityStep(product);

    return (
      <Row
        key={product.id + '-' + index}
        className="ProposalCheckoutPageContent__product-row"
      >
        {!isMobile && (
          <Col
            className="ProposalCheckoutPageContent__product-image"
            md="2"
            lg="1"
          >
            {product.isPhysicalProduct && (
              <ProductImage
                product={product}
                productImage={this.createProductImage(
                  product.image_url,
                  product.id
                )}
                size="small"
                forceAspectRatio={false}
              />
            )}
          </Col>
        )}
        {smallScreen && this.renderProductHeaderCol(ProductHeaders.name)}
        <Col xs={smallScreen && '9'} md="5" lg="6">
          <div className={'ProposalCheckoutPageContent__product-name-wrapper'}>
            {this.renderProductLink(product)}
          </div>
          {product.variations && product.variations.length > 0 && (
            <ul>
              {product.variations.map((variation, index) => (
                <li key={index}>{variation}</li>
              ))}
            </ul>
          )}
        </Col>
        {isDesktop && (
          <Col lg="1">
            {product.final_unit_price && (
              <Price price={product.final_unit_price} />
            )}
          </Col>
        )}
        {smallScreen && this.renderProductHeaderCol(ProductHeaders.quantity)}
        <Col xs={smallScreen && '9'} md="5" lg="4">
          <div className="ProposalCheckoutPageContent__quantity">
            <QuantityFieldGroup
              key={product.product_id}
              className="ProposalCheckoutPageContent__quantity-group"
              disabled={false}
              field={quantityField}
              defaultValue={defaultValue}
              unit={product.unitName}
              quantityStep={product.getQuantityStep(product)}
              onUpdateProductQuantity={(value) => {
                this.updateQuantity(`quantity-${product.id}`, value);
              }}
              maxQuantity={99999}
            >
              {showPrices && (
                <div className="ProposalCheckoutPageContent__product-total">
                  <FormattedMessage
                    {...globalTranslations.totalPriceSentenceAbbr}
                    values={{
                      value: <Price price={product.total_price} />,
                    }}
                  />
                </div>
              )}
            </QuantityFieldGroup>
            <div className="ProposalCheckoutPageContent__remove-button">
              {this.renderRemoveButton(product.id)}
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  parseExtraFields = () => {
    const { proposal } = this.props.proposalStore;
    const extraFields = [];

    if (proposal.extra_fields.length === 0) {
      return extraFields;
    }

    proposal.extra_fields.forEach((extraField) => {
      extraFields.push(
        CustomFormField.create({
          name: extraField.name,
          required: false,
          value: extraField.value,
          id: FormFieldId.EXTRA_MESSAGE,
          type: extraField.type,
        })
      );
    });
    return extraFields;
  };

  getForm = () => {
    const { customer_fields } = this.props.proposalStore.proposal;
    const extraFields = this.parseExtraFields();

    return (
      <Row>
        <Col xs="12" md="6">
          <CustomForm
            fields={customer_fields.slice()}
            onChange={this.updateContactInformation}
            formName="ContactInformation"
          />
        </Col>
        <Col xs="12" md="6">
          {extraFields.length > 0 && (
            <CustomForm
              fields={extraFields}
              onChange={this.updateExtraField}
              formName="ExtraFields"
            />
          )}
        </Col>
      </Row>
    );
  };

  isBusy = () => {
    return this.props.loading || this.props.removing;
  };

  isDisabled = () => {
    return this.isBusy() || this.props.disabled || this.state.unsentChanges;
  };

  renderRemoveButton = (id) => {
    return (
      <Button
        color="secondary"
        disabled={this.isDisabled()}
        onClick={() => this.removeProduct(id)}
      >
        <Icon name="trash" />
      </Button>
    );
  };

  renderConfirmButton = () => {
    return (
      <Button
        color="primary"
        className="ProposalCheckoutPageContent__confirm-button"
        disabled={this.isDisabled()}
        onClick={this.confirmProposal}
      >
        <Icon
          name={this.isBusy() ? 'circle-o-notch' : 'envelope-o'}
          spin={this.isBusy()}
        />
        <FormattedMessage {...globalTranslations.sendTitle} />
      </Button>
    );
  };

  render() {
    const { proposalStore } = this.props;
    const emptyContent = proposalStore.proposal.products.length === 0;

    return (
      <Container className="ProposalCheckoutPageContent__container">
        {this.renderTopWysiwyg()}
        <PageTitle>
          <FormattedMessage
            id="checkout.proposalTitle"
            defaultMessage="Proposal content"
          />
        </PageTitle>
        {emptyContent ? (
          this.renderEmptyProposal()
        ) : (
          <div className="ProposalCheckoutPageContent__content">
            {this.renderProductList()}
            {this.renderProposalForm()}
            {this.renderConfirmButton()}
          </div>
        )}
      </Container>
    );
  }
}

ProposalCheckoutPageContent.propTypes = {
  proposalStore: modelOf(ProposalStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  loading: PropTypes.bool.isRequired,
  removing: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onOrderConfirm: PropTypes.func.isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  onProductRemove: PropTypes.func.isRequired,
};

export default injectIntl(
  inject('proposalStore', 'uiStore')(withRouter(ProposalCheckoutPageContent))
);
